import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  OutlineButton, 
  Badge, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableHeader,
  FlexContainer,
  Input,
  Select,
  Pagination,
  PageButton
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { OfficeBudget, OfficeBudgetCategory, BudgetCategoryType } from '../../types';
import { FaCheckCircle, FaTimesCircle, FaEye, FaDownload } from 'react-icons/fa';
import apiService from '../../services/api';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  background-color: white;
  padding: 1rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  label {
    font-weight: 500;
    color: var(--dark-gray);
    white-space: nowrap;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const StatusBadge = styled(Badge)`
  min-width: 100px;
  text-align: center;
`;

const AmountText = styled.span`
  font-weight: 500;
`;

const DateText = styled.span`
  color: var(--dark-gray);
  font-size: 0.85rem;
`;

const ApprovalChain = styled.div`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--dark-gray);
`;

const ApprovalStep = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  
  .step-icon {
    color: ${props => 
      props.status === 'Approved' ? 'var(--success-color)' :
      props.status === 'Rejected' ? 'var(--danger-color)' :
      props.status === 'Pending' ? 'var(--warning-color)' : 'var(--dark-gray)'
    };
  }
`;



const OfficeBudgetApproval: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load real office budget requests
  useEffect(() => {
    const loadRequests = async () => {
      try {
        setLoading(true);
        const response = await apiService.getOfficeBudgetRequests({ limit: 1000 });
        console.log('Office budget API response:', response); // Debug log
        if (response.success && response.data) {
          setRequests(response.data);
        } else {
          console.error('API response missing data:', response);
          setRequests([]);
        }
      } catch (error) {
        console.error('Error loading office budget requests:', error);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadRequests();
  }, []);

  // Filter requests based on user role
  useEffect(() => {
    let filtered = requests;
    
    // Filter by user role permissions
    if (user?.role === 'Store-Keeper (Logistics)') {
      filtered = filtered.filter(req => req.requesterRole === 'Store-Keeper (Logistics)');
    } else if (user?.role === 'HR (Admin)') {
      filtered = filtered.filter(req => req.status === 'Pending HR Approval' || req.status === 'Approved by HR');
    } else if (user?.role === 'MD' || user?.role === 'TD') {
      filtered = filtered.filter(req => req.status === 'Approved by HR' || req.status === 'Approved by MD');
    } else if (user?.role === 'Accountant') {
      filtered = filtered.filter(req => req.status === 'Approved by MD');
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(req => 
        req.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.purpose.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.requester.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(req => req.status === statusFilter);
    }
    
    setFilteredRequests(filtered);
    setCurrentPage(1);
  }, [requests, searchTerm, statusFilter, user?.role]);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' => {
    switch (status) {
      case 'Approved by MD':
        return 'success';
      case 'Approved by HR':
        return 'info';
      case 'Pending HR Approval':
        return 'warning';
      case 'Rejected':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-TZ', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleApprove = async (requestId: string) => {
    try {
      // Find the request to get its details
      const request = requests.find(req => req.id === requestId);
      if (!request) {
        console.error('Request not found');
        return;
      }

      // Prepare approval data
      const approvalData = {
        action: 'approve',
        comments: `Approved by ${user?.role}`
      };

      // Make API call to approve the request
      const response = await apiService.approveOfficeBudgetRequest(requestId, approvalData);
      
      if (response.success) {
        // Update local state with the response data
        setRequests(prev => prev.map(req => {
          if (req.id === requestId) {
            return {
              ...req,
              ...response.data,
              updatedAt: new Date()
            };
          }
          return req;
        }));
        
        console.log(`Request ${requestId} approved by ${user?.role}`);
      } else {
        console.error('Failed to approve request:', response.message);
      }
      
      // Create accounting entry if Accountant approved
      if (user?.role === 'Accountant') {
        const accountingEntry = {
          description: `Office Budget Request - ${request.title || request.description}`,
          amount: request.amount,
          currency: request.currency || 'TZS',
          type: 'Income',
          category: 'Office Budget',
          account: 'Office Income',
          date: new Date(),
          approved: true,
          approvedBy: user?.id,
          approvedAt: new Date(),
          reference: `Budget-${requestId}`
        };
        
        // Create the accounting entry
        try {
          await apiService.createAccountingEntry(accountingEntry);
          console.log('Accounting entry created for approved budget request');
        } catch (error) {
          console.error('Error creating accounting entry:', error);
        }
      }
    } catch (error) {
      console.error('Error approving request:', error);
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      // Prepare rejection data
      const rejectionData = {
        action: 'reject',
        comments: `Rejected by ${user?.role}`
      };

      // Make API call to reject the request
      const response = await apiService.approveOfficeBudgetRequest(requestId, rejectionData);
      
      if (response.success) {
        // Update local state with the response data
        setRequests(prev => prev.map(req => {
          if (req.id === requestId) {
            return {
              ...req,
              ...response.data,
              updatedAt: new Date()
            };
          }
          return req;
        }));
        
        console.log(`Request ${requestId} rejected by ${user?.role}`);
      } else {
        console.error('Failed to reject request:', response.message);
      }
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const canApprove = (request: any) => {
    if (user?.role === 'HR (Admin)') {
      return request.status === 'Pending HR Approval';
    } else if (user?.role === 'MD' || user?.role === 'TD') {
      return request.status === 'Approved by HR';
    } else if (user?.role === 'Accountant') {
      return request.status === 'Approved by MD';
    }
    return false;
  };

  const canReject = (request: any) => {
    return canApprove(request);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

  return (
    <div>
      <PageHeader>
        <h1>Office Budget Approval</h1>
        <ActionButtons>
          <Button as={Link} to="/office/budget">
            Back to Budget
          </Button>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <SearchContainer>
          <Input
            type="text"
            placeholder="Search requests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterGroup>
          <label>Status:</label>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="Pending HR Approval">Pending HR Approval</option>
            <option value="Approved by HR">Approved by HR</option>
            <option value="Approved by MD">Approved by MD</option>
            <option value="Rejected">Rejected</option>
          </Select>
        </FilterGroup>
      </FilterSection>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          Loading office budget requests...
        </div>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Request ID</TableHeader>
                <TableHeader>Requester</TableHeader>
                <TableHeader>Purpose</TableHeader>
                <TableHeader>Amount</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Created</TableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
          <tbody>
            {currentItems.map((request) => (
              <TableRow key={request.id}>
                <TableCell>
                  <strong>{request.id}</strong>
                  <ApprovalChain>
                    {request.approvalChain.map((step: any, index: number) => (
                      <ApprovalStep key={index} status={step.status}>
                        <span className="step-icon">
                          {step.status === 'Approved' ? '✓' : 
                           step.status === 'Rejected' ? '✗' : 
                           step.status === 'Pending' ? '⏳' : '○'}
                        </span>
                        {step.role}
                      </ApprovalStep>
                    ))}
                  </ApprovalChain>
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{request.requester}</strong>
                    <br />
                    <small>{request.requesterRole}</small>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <strong>{request.purpose}</strong>
                    <br />
                    <small>{request.justification}</small>
                  </div>
                </TableCell>
                <TableCell>
                  <AmountText>{formatAmount(request.amount, request.currency)}</AmountText>
                </TableCell>
                <TableCell>
                  <StatusBadge variant={getStatusVariant(request.status)}>
                    {request.status}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <DateText>{formatDate(request.createdAt)}</DateText>
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <Button
                      as={Link}
                      to={`/office/budget-approval/${request.id}`}
                    >
                      View
                    </Button>
                    
                    {canApprove(request) && (
                      <Button
                        onClick={() => handleApprove(request.id)}
                      >
                        Approve
                      </Button>
                    )}
                    
                    {canReject(request) && (
                      <Button
                        onClick={() => handleReject(request.id)}
                      >
                        Reject
                      </Button>
                    )}
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </tbody>
            </Table>
          </TableContainer>
        )}

      {totalPages > 1 && (
        <Pagination>
          <PageButton
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </PageButton>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
            <PageButton
              key={number}
              onClick={() => paginate(number)}
              active={currentPage === number}
            >
              {number}
            </PageButton>
          ))}
          
          <PageButton
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </PageButton>
        </Pagination>
      )}

      {currentItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>No budget requests found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default OfficeBudgetApproval; 